import { Checkbox, Divider, Stack, TextField, Typography, useTheme } from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { MinusSquareIcon } from "@phosphor-icons/react/dist/csr/MinusSquare";
import { MouseEvent, useState } from "react";
import { SourceLeaf, SourceNode } from "../common/FilterPublishedArtifacts";
import { ClearThisFilter } from "./Common";

const truncateMiddle = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    const front = text.slice(0, maxLength / 2);
    const back = text.slice(-maxLength / 2);
    return `${front} ... ${back}`;
  }
  return text;
};

const ProjectWithChild = ({
  node,
  handleClick,
  allChildrenSelected,
  checked,
}: {
  node: SourceNode;
  handleClick: (node: SourceNode, child?: SourceLeaf) => void;
  allChildrenSelected: boolean;
  checked?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const theme = useTheme();
  const handleClickOpen = (e: MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prevState) => !prevState);
  };
  return (
    <>
      <Stack
        direction="row"
        onClick={(event) => {
          event.stopPropagation();
          handleClick(node);
        }}
        sx={{
          pr: "15px",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        data-testid={`project-${node.title}`}
      >
        <Stack direction="row" sx={{ gap: "8px" }}>
          <Checkbox
            key={node.title}
            checked={checked}
            checkedIcon={
              !allChildrenSelected ? (
                <MinusSquareIcon color={theme.palette.primary50.main} weight="fill" size={24} />
              ) : undefined
            }
          />
          <Typography variant="body.small.semibold">{node.title}</Typography>
        </Stack>
        {isOpen ? (
          <CaretUpIcon size={24} onClick={handleClickOpen} />
        ) : (
          <CaretDownIcon size={24} onClick={handleClickOpen} />
        )}
      </Stack>
      {isOpen ? (
        <>
          {node.children?.map((child, index) =>
            child.show ? (
              <Child
                key={`${child.title}-${index}`}
                node={child}
                handleClick={() => handleClick(node, child)}
                checked={checked}
              />
            ) : null
          )}
        </>
      ) : null}
    </>
  );
};

const isLastChild = (node: SourceNode) => {
  return node.children.length === 1;
};

const Child = ({ node, handleClick, checked }: { node: SourceLeaf; handleClick: () => void; checked?: boolean }) => {
  const theme = useTheme();
  return (
    <Stack
      onClick={(event) => {
        event.stopPropagation();
        handleClick();
      }}
      sx={{
        gap: "4px",
        cursor: "pointer",
      }}
      data-testid={`child-${node.title}`}
    >
      <Stack direction="row" sx={{ gap: "8px", pl: "28px" }}>
        <Checkbox key={node.title} checked={checked} />
        <Stack sx={{ width: "24px", height: "24px" }}>
          {node.destination ? (
            <CloudArrowUpIcon size={24} color={theme.palette.text40.main} />
          ) : (
            <CloudArrowDownIcon size={24} color={theme.palette.text40.main} />
          )}
        </Stack>
        <Typography variant="body.medium.regular">{node.title}</Typography>
      </Stack>

      {node.destination ? (
        <Stack sx={{ pl: "60px" }}>
          <Typography variant="body.small.regular" sx={{ color: "text40.main" }}>
            {truncateMiddle(node.destination, 116)}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
};

const ProjectsFilter = ({
  selectedNodesInFilter,
  nonSelectedNodesInFilter,
  onSelectCallback,
  onClearThisFilter,
}: {
  selectedNodesInFilter: SourceNode[];
  nonSelectedNodesInFilter: SourceNode[];
  onSelectCallback: (node: SourceLeaf[], takeOut?: boolean) => void;
  onClearThisFilter: () => void;
}) => {
  const [search, setSearch] = useState<string>("");
  const [nonSelectedNodes, setNonSelectedNodes] = useState<SourceNode[]>(nonSelectedNodesInFilter);
  const [selectedNodes, setSelectedNodes] = useState<SourceNode[]>(selectedNodesInFilter);
  const [prevNonSelected, setPrevNonSelected] = useState(nonSelectedNodesInFilter);
  const [prevSelected, setPrevSelected] = useState(selectedNodesInFilter);

  if (prevNonSelected !== nonSelectedNodesInFilter || prevSelected !== selectedNodesInFilter) {
    setPrevNonSelected(nonSelectedNodesInFilter);
    setPrevSelected(selectedNodesInFilter);
    setNonSelectedNodes(nonSelectedNodesInFilter);
    setSelectedNodes(selectedNodesInFilter);
  }

  const childrenToShow = (node: SourceNode, value: string) => {
    return node.children.map((n) => {
      const showNode = n.title.toLowerCase().includes(value.toLowerCase());
      return {
        ...n,
        show: showNode,
      };
    });
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    const newNonSelectedNodes = nonSelectedNodes.map((n) => {
      const matchesParent = n.title.toLowerCase().includes(value.toLowerCase());
      const newChildren = childrenToShow(n, value).map((child) => ({
        ...child,
        show: matchesParent || child.show,
      }));

      return {
        ...n,
        show: value === "" || matchesParent || newChildren.some((c) => c.show),
        children: newChildren,
      };
    });
    setNonSelectedNodes(newNonSelectedNodes);

    const newSelectedNodes = selectedNodes.map((n) => {
      const matchesParent = n.title.toLowerCase().includes(value.toLowerCase());
      const newChildrens = childrenToShow(n, value).map((child) => ({
        ...child,
        show: matchesParent || child.show,
      }));

      return {
        ...n,
        show: value === "" || matchesParent || newChildrens.some((c) => c.show),
        children: newChildrens,
      };
    });
    setSelectedNodes(newSelectedNodes);
  };

  const handleClickNonSelectedNode = (node: SourceNode, child?: SourceLeaf) => {
    const newNode = selectedNodes.find((n) => {
      return n.title === node.title;
    });
    if (!child || isLastChild(node)) {
      // if project is clicked
      if (newNode) {
        newNode.children.push(...node.children);
      } else {
        setSelectedNodes([...selectedNodes, node]);
      }
      setNonSelectedNodes(nonSelectedNodes.filter((n) => n.title !== node.title));
      const children = node.children;
      const newSelectedNodes = [node, ...children];
      onSelectCallback(newSelectedNodes);
    } else {
      // child is clicked
      if (newNode) {
        newNode.children.push(child);
      } else {
        setSelectedNodes([
          ...selectedNodes,
          {
            ...node,
            children: [child],
          },
        ]);
      }

      const newNonSelectedNodes = nonSelectedNodes.map((n) => {
        if (n.title === node.title) {
          return {
            ...n,
            children: n.children.filter((c) => c.title !== child.title),
          };
        }
        return n;
      });
      setNonSelectedNodes(newNonSelectedNodes);
      onSelectCallback([child]);
    }
  };

  const handleClickSelectedNode = (node: SourceNode, child?: SourceLeaf) => {
    const newNonSelectedNodes = nonSelectedNodes.find((n) => {
      return n.title === node.title;
    });
    if (!child || isLastChild(node)) {
      // if project is clicked or last child is clicked
      setSelectedNodes(selectedNodes.filter((n) => n.title !== node.title));
      if (newNonSelectedNodes) {
        newNonSelectedNodes.children.push(...node.children);
        setNonSelectedNodes(nonSelectedNodes);
      } else {
        setNonSelectedNodes([
          ...nonSelectedNodes,
          {
            ...node,
          },
        ]);
      }

      const children = node.children;
      const nodesToRemove = [node, ...children];
      onSelectCallback(nodesToRemove, true);
    } else {
      const newSelectedNodes = selectedNodes.map((n) => {
        if (n.title === node.title) {
          return {
            ...n,
            children: n.children.filter((c) => c.title !== child.title),
          };
        }
        return n;
      });
      setSelectedNodes(newSelectedNodes);
      const newNonSelectedNodes = nonSelectedNodes.find((n) => {
        return n.title === node.title;
      });
      if (newNonSelectedNodes) {
        newNonSelectedNodes.children.push(child);
        setNonSelectedNodes(nonSelectedNodes);
      } else {
        setNonSelectedNodes([
          ...nonSelectedNodes,
          {
            ...node,
            children: [child],
          },
        ]);
      }
      onSelectCallback([child], true);
    }
  };

  const searchDoesNotMatch =
    selectedNodes.filter((n) => n.show).length === 0 &&
    nonSelectedNodes.filter((n) => n.show).length === 0 &&
    search !== "";

  return (
    <Stack sx={{ width: "100%", gap: "20px" }}>
      <TextField
        placeholder="Search project name"
        fullWidth
        onChange={handleTextChange}
        value={search}
        focused={false}
        autoComplete="off"
        data-testid="search-projects"
      />
      <Stack sx={{ height: "430px" }}>
        {searchDoesNotMatch ? (
          <Typography variant="body.small.regular" sx={{ color: "text40.main" }}>
            No results, try another word.
          </Typography>
        ) : (
          <Stack sx={{ gap: "8px", height: "100%" }}>
            {selectedNodes.length ? (
              <>
                <Typography variant="body.small.semibold" sx={{ color: "text40.main" }}>
                  Selected
                </Typography>
                <Stack
                  sx={{
                    gap: "8px",
                    maxHeight: "180px",
                    overflowY: "auto",
                    flexShrink: 0,
                  }}
                >
                  {selectedNodes.map((node, index) =>
                    node.show ? (
                      <Stack sx={{ gap: "12px" }} key={`${node.title}-${index}`} data-testid="selected-node">
                        <ProjectWithChild
                          node={node}
                          handleClick={handleClickSelectedNode}
                          checked
                          allChildrenSelected={nonSelectedNodes.filter((n) => n.title === node.title).length === 0}
                        />
                      </Stack>
                    ) : null
                  )}
                </Stack>
                <Divider orientation="horizontal" />
              </>
            ) : null}
            {nonSelectedNodes.length ? (
              <>
                <Typography variant="body.small.semibold" sx={{ color: "text40.main" }}>
                  Non selected
                </Typography>
                <Stack
                  sx={{
                    gap: "8px",
                    overflowY: "auto",
                    flexGrow: 1,
                  }}
                >
                  {nonSelectedNodes.map((node, index) =>
                    node.show ? (
                      <Stack sx={{ gap: "12px" }} key={`${node.title}-${index}`} data-testid="non-selected-node">
                        <ProjectWithChild
                          node={node}
                          handleClick={handleClickNonSelectedNode}
                          allChildrenSelected={selectedNodes.filter((n) => n.title === node.title).length === 0}
                        />
                      </Stack>
                    ) : null
                  )}
                </Stack>
              </>
            ) : null}
          </Stack>
        )}
      </Stack>
      {selectedNodes.length ? (
        <Stack sx={{ borderTop: "1px solid", borderColor: "text20.main", height: "40px", p: "16px" }}>
          <ClearThisFilter onClick={onClearThisFilter} />
        </Stack>
      ) : null}
    </Stack>
  );
};

export { ProjectsFilter };
